import React from "react";
import { useFormikContext } from "formik";
import { Icon } from "@iconify/react";
import { VStack } from "../../../../components/utils";
import {
  EmployeeOfferDetails,
  GrantOfferDetails,
} from "../../../../types/Transaction";
import { ConversionMethodology } from "../../../../types/Exercise";
import { formatCurrency } from "../../../../utils/currencyFormatter";
import Tooltip from "../../../../components/shared/Tooltip";

export type SummaryCardType =
  | "employees"
  | "optionsExercised"
  | "exercisePricePaid"
  | "shares"
  | "taxPaid";

export interface SummaryTransactionCardsProps {
  cardTypes: SummaryCardType[];
  currencyType?: string;
}

type CardConfigValue =
  | string
  | number
  | ((details: any, currencyType: string) => string | number);

type CardConfigItem = {
  header: CardConfigValue;
  mainHeader: CardConfigValue;
  subHeader: CardConfigValue;
  value: CardConfigValue;
  headerTooltip?: CardConfigValue;
  mainHeaderTooltip?: CardConfigValue;
  subHeaderTooltip?: CardConfigValue;
};

type CardConfigs = Record<SummaryCardType, CardConfigItem>;

const CARD_CONFIGS: CardConfigs = {
  employees: {
    header: "Employees",
    mainHeader: "",
    subHeader: (details: any, currencyType: string) =>
      `Total Eligible: ${details.employeesEligible?.toLocaleString(
        currencyType
      )}`,
    value: (details: any, currencyType: string) =>
      details.totalEmployeesAccepted?.toLocaleString(currencyType),
  },
  optionsExercised: {
    header: "Options Exercised",
    mainHeader: "",
    subHeader: (details: any, currencyType: string) =>
      `Total Eligible Options: ${details.totalEligibleOptions?.toLocaleString(
        currencyType
      )}`,
    value: (details: any, currencyType: string) =>
      details.willingnessGiven?.toLocaleString(currencyType),
  },
  exercisePricePaid: {
    header: (details: any) =>
      details.isPaymentEnabled
        ? "Exercise Price Paid"
        : "Exercise Price Payable",
    headerTooltip: (details: any) =>
      details.isPaymentEnabled
        ? "Amount successfully collected as exercise price payments."
        : "Payment is disabled, so this shows payable exercise amount.",
    mainHeader: "",
    subHeader: (details: any, currencyType: string) =>
      details.isPaymentEnabled
        ? `Total Exercise Price: ${formatCurrency(
            details.totalEligibleOptionsExercisePrice || 0,
            currencyType
          )}`
        : "Payment is disabled",
    value: (details: any, currencyType: string) =>
      formatCurrency(
        details.isPaymentEnabled
          ? details.totalExercisePricePaid || 0
          : details.totalEligibleOptionsExercisePrice || 0,
        currencyType
      ),
  },
  shares: {
    header: "Shares Issued",
    mainHeader: "",
    headerTooltip:
      "Issued shares based on willingness and conversion methodology.",
    subHeader: (details: any, currencyType: string) =>
      `Total Shares Eligible: ${details.totalSharesEligible?.toLocaleString(
        currencyType
      )}`,
    value: (details: any, currencyType: string) =>
      details.totalSharesIssued?.toLocaleString(currencyType),
  },
  taxPaid: {
    header: (details: any) =>
      details.isPaymentEnabled ? "Total Tax Paid" : "Total Tax Payable",
    headerTooltip: (details: any) =>
      details.isPaymentEnabled
        ? "Amount successfully collected as tax payments."
        : "Payment is disabled, so this shows payable tax amount.",
    mainHeader: "",
    subHeader: (details: any, currencyType: string) =>
      details.isPaymentEnabled
        ? `Total Tax Price: ${formatCurrency(
            details.totalTaxPayable || 0,
            currencyType
          )}`
        : "Payment is disabled",
    value: (details: any, currencyType: string) =>
      formatCurrency(
        details.isPaymentEnabled
          ? details.taxPaid || 0
          : details.totalTaxPayable || 0,
        currencyType
      ),
  },
};

export const SummaryTransactionCards = ({
  cardTypes,
  currencyType,
}: SummaryTransactionCardsProps) => {
  const formik = useFormikContext<any>();
  const details = React.useMemo(() => {
    const includedPaymentStatuses = new Set(["SUCCESS"]);

    const getPaymentTotalByType = (
      employee: any,
      paymentType: "exercise price" | "tax price"
    ) =>
      (employee.paymentDetails || []).reduce(
        (paymentSum: number, payment: any) => {
          if (payment?.paymentType?.toLowerCase() !== paymentType) {
            return paymentSum;
          }
          if (!includedPaymentStatuses.has(payment?.paymentStatus)) {
            return paymentSum;
          }
          return paymentSum + (Number(payment?.amount) || 0);
        },
        0
      );

    const acceptedStates = [
      "ACCEPTED",
      "WILLINGNESS_GIVEN",
      "WILLINGNESS_ACCEPTED",
      "COMPLETED",
      "PAYMENT_COMPLETED",
      "PAYMENT_PENDING",
      "PAYMENT_FAILED",
      "SIGN_GIVEN",
      "PENDING_EXERCISED",
    ];
    const selectedEmployeesList = formik.values.selectedEmployeesList || [];
    const previewList = formik.values.previewList || [];
    const isPaymentEnabled =
      !!formik.values?.monetaryDetails?.transactionPaymentEnabled;
    const totalEmployeesAccepted = previewList.filter((employee: any) =>
      acceptedStates.includes(employee.offerState || "DRAFT")
    ).length;
    const employeesEligible = previewList.length || 0;
    const willingnessGiven = selectedEmployeesList.reduce(
      (sum: number, employee: any) =>
        sum +
        (employee.grantRelatedDetails?.reduce(
          (grantSum: number, grant: any) => grantSum + (grant.willingness || 0),
          0
        ) || 0),
      0
    );
    const totalEligibleOptions = previewList.reduce(
      (sum: number, employee: any) =>
        sum +
        (employee.grantRelatedDetails?.reduce(
          (grantSum: number, grant: any) =>
            grantSum + (grant.eligibleOptions || 0),
          0
        ) || 0),
      0
    );
    const totalSharesEligible = previewList.reduce(
      (sum: number, employee: EmployeeOfferDetails) =>
        sum +
        (employee.grantRelatedDetails?.reduce(
          (grantSum: number, grant: GrantOfferDetails) =>
            grantSum +
            (grant.conversionMethodology === ConversionMethodology.ROUND_DOWN
              ? Math.floor(
                  (grant.eligibleOptions || 0) * (grant.conversionRatio || 1)
                )
              : Math.ceil(
                  (grant.eligibleOptions || 0) * (grant.conversionRatio || 1)
                )),
          0
        ) || 0),
      0
    );
    const totalSharesIssued = selectedEmployeesList
      ?.filter((emp: EmployeeOfferDetails) => emp.offerState === "COMPLETED")
      ?.reduce(
        (sum: number, employee: EmployeeOfferDetails) =>
          sum +
          (employee.grantRelatedDetails?.reduce(
            (grantSum: number, grant: GrantOfferDetails) =>
              grantSum +
              (grant.conversionMethodology === ConversionMethodology.ROUND_DOWN
                ? Math.floor(
                    (grant.willingness || 0) * (grant.conversionRatio || 1)
                  )
                : Math.ceil(
                    (grant.willingness || 0) * (grant.conversionRatio || 1)
                  )),
            0
          ) || 0),
        0
      );
    const totalEligibleOptionsExercisePrice = selectedEmployeesList.reduce(
      (sum: number, employee: any) =>
        sum +
        (employee.grantRelatedDetails?.reduce(
          (grantSum: number, grant: any) =>
            grantSum + (grant.willingness || 0) * (grant.strikePrice || 0),
          0
        ) || 0),
      0
    );
    const totalTaxPayable = selectedEmployeesList.reduce(
      (sum: number, employee: any) => {
        const taxAmount = Number(
          employee?.transactionCompensationDetails?.totalTaxAmount
        );
        return sum + (Number.isFinite(taxAmount) ? taxAmount : 0);
      },
      0
    );
    const totalExercisePricePaid = selectedEmployeesList.reduce(
      (sum: number, employee: any) =>
        sum + getPaymentTotalByType(employee, "exercise price"),
      0
    );
    const taxPaid = selectedEmployeesList.reduce(
      (sum: number, employee: any) =>
        sum + getPaymentTotalByType(employee, "tax price"),
      0
    );
    return {
      isPaymentEnabled,
      totalEmployeesAccepted,
      employeesEligible,
      willingnessGiven,
      totalEligibleOptions,
      totalEligibleOptionsExercisePrice,
      totalTaxPayable,
      totalExercisePricePaid,
      totalSharesEligible,
      totalSharesIssued,
      taxPaid,
    };
  }, [formik.values]);

  const currency = currencyType || "en-IN";

  const resolveConfigValue = React.useCallback(
    (value: CardConfigValue | undefined) => {
      if (value === undefined || value === null) return "";
      if (typeof value === "function") {
        return value(details, currency);
      }
      return value;
    },
    [details, currency]
  );

  const renderTextWithTooltip = (
    textValue: string | number,
    tooltipValue: string | number,
    textClassName: string,
    verticalAlign: "center" | "start" = "center"
  ) => {
    const hasTooltip = Boolean(`${tooltipValue || ""}`.trim());
    const alignClass =
      verticalAlign === "start" ? "items-start" : "items-center";
    return (
      <div className="w-full text-center leading-none">
        <span
          className={`inline-flex ${alignClass} justify-center gap-1 align-middle`}
        >
          <span className={textClassName}>{textValue}</span>
          {hasTooltip && (
            <Tooltip
              text={`${tooltipValue}`}
              _className="text-left w-64 max-w-xs whitespace-normal"
              className="left-1/2 -translate-x-1/2 right-auto"
            >
              <Icon
                icon="material-symbols:info-outline-rounded"
                width={14}
                className={`text-gray-400 cursor-pointer block shrink-0 ${
                  verticalAlign === "start" ? "mt-2" : ""
                }`}
              />
            </Tooltip>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="flex gap-4 pt-6 text-base font-semibold text-center text-black-501">
      {cardTypes.map((type, idx) => {
        const config = CARD_CONFIGS[type];
        if (!config) return null;

        const headerValue = resolveConfigValue(config.header);
        const valueValue = resolveConfigValue(config.value);
        const mainHeaderValue = resolveConfigValue(config.mainHeader);
        const subHeaderValue = resolveConfigValue(config.subHeader);

        const headerTooltipValue = resolveConfigValue(config.headerTooltip);
        const mainHeaderTooltipValue = resolveConfigValue(
          config.mainHeaderTooltip
        );
        const subHeaderTooltipValue = resolveConfigValue(
          config.subHeaderTooltip
        );

        return (
          <VStack
            key={idx}
            className="items-center justify-center p-4 text-center border border-gray-200 rounded-lg shadow-box"
          >
            {renderTextWithTooltip(
              headerValue,
              headerTooltipValue,
              "p-1 text-base font-medium form-label",
              "start"
            )}
            {valueValue}
            {renderTextWithTooltip(
              mainHeaderValue,
              mainHeaderTooltipValue,
              "p-1 text-sm font-normal form-label"
            )}
            {renderTextWithTooltip(
              subHeaderValue,
              subHeaderTooltipValue,
              "p-1 px-4 text-xs font-normal text-gray-400"
            )}
          </VStack>
        );
      })}
    </div>
  );
};

export default SummaryTransactionCards;
